import { DateTime } from 'luxon';
import { ConfigurationFormCoordinatorService } from '../configuration-form-coordinator.service';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ExportInterfaceActivationEmailDTO,
  ExportInterfaceActivationEmailTemplateDTO,
  ExportInterfaceConfigurationDataSelectionType,
  ExportInterfaceConfigurationDTO,
  ExportInterfaceConfigurationMode,
  ExportInterfaceConfigurationTransferType,
  ExportInterfaceConfigurationType,
  GenerateActivationEmailsDTO,
} from 'src/app/base/models/models.swagger.generated';
import { ActivationEmailTemplatesOverviewService } from '../../activation-email-templates-overview/activation-email-templates-overview.service';
import { HtmlEditorService, ImageService, ImageSettingsModel, LinkService, ToolbarService } from '@syncfusion/ej2-angular-richtexteditor';
import { ActivationEmailService } from '../../../activation-email.service';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ActivationEmailPreviewComponent } from './activation-email-preview/activation-email-preview.component';
import { ConfigurationAccessComponent } from './configuration-access/configuration-access.component';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { YesNoGroupButtonComponent } from '../../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { NgClass } from '@angular/common';
import { EnumDropDownListComponent } from '../../../../../base/shared/enum-drop-down-list/enum-drop-down-list.component';
import { AppErrorMessagesComponent } from '../../../../../base/error-messages/app-error-messages.component';
import { ButtonGroupComponent } from 'src/app/base/shared/button-group/button-group.component';

@Component({
    selector: 'app-configuration-form-general',
    templateUrl: './configuration-form-general.component.html',
    styleUrls: ['./configuration-form-general.component.scss'],
    providers: [ToolbarService, LinkService, ImageService, HtmlEditorService],
    imports: [ReactiveFormsModule, AppErrorMessagesComponent, EnumDropDownListComponent, YesNoGroupButtonComponent, NgClass, DropDownListModule, ConfigurationAccessComponent, ActivationEmailPreviewComponent, TranslateModule, ButtonGroupComponent]
})
export class ConfigurationFormGeneralComponent implements OnInit, OnDestroy {

  @Input() public data: ExportInterfaceConfigurationDTO;

  public TransferType = ExportInterfaceConfigurationTransferType;
  public Type = ExportInterfaceConfigurationType;
  public DataSelectionType = ExportInterfaceConfigurationDataSelectionType;
  public activationEmailTemplates: ExportInterfaceActivationEmailTemplateDTO[];
  public fields = { text: 'templateName', value: 'id' };
  public insertImageSettings: ImageSettingsModel = { saveFormat: 'Base64' };

  public showPreview: boolean = false;

  public ExportInterfaceConfigurationMode = ExportInterfaceConfigurationMode;

  public get sendActivationEmailAllowed(): boolean {
    return this.configurationFormService.configurationDetailsDTO?.active === true;
  }

  public get configurationFormGeneral(): UntypedFormGroup {
    return this.configurationFormService.configurationFormGeneral;
  }

  public get configurationFormEmail(): UntypedFormGroup {
    return this.configurationFormService.configurationFormEmail;
  }

  public get configurationFormREST(): UntypedFormGroup {
    return this.configurationFormService.configurationFormREST;
  }

  public get showUserAccessList(): boolean {
    if (this.configurationFormService.isTemplateMode) {
      return false;
    }
    const transferType = this.configurationFormService.transferType;
    return transferType == ExportInterfaceConfigurationTransferType.REST
      || transferType == ExportInterfaceConfigurationTransferType.SFTPServer
      || transferType == ExportInterfaceConfigurationTransferType.Email
      || transferType == ExportInterfaceConfigurationTransferType.SFTPServerOnly;
  }

  public get enablePreviewButton(): boolean {
    return this.configurationFormService.configurationDetailsDTO?.id && this.configurationFormGeneral.get('activationEmailTemplateId').value;
  }

  public get showEmailPreviewButton(): boolean {
    return !this.configurationFormService.isTemplateMode;
  }

  public isTemplateMode(): boolean {
    return this.configurationFormService.isTemplateMode;
  }

  public isResponseMode() {
    return this.configurationFormService.configurationFormGeneral.controls.mode.value === ExportInterfaceConfigurationMode.Response;
  }

  public activationEmails: ExportInterfaceActivationEmailDTO[];
  public currentActivationEmailIndex: number;
  public activationEmailForm: UntypedFormGroup;
  public showActivationEmailOption: boolean = true;

  public excludeTypeEnumMembers: number[] = [];
  public excludeTransferTypeEnumMembers: number[] = [];

  private _subscription: Subscription = new Subscription();

  constructor(
    private configurationFormService: ConfigurationFormCoordinatorService,
    private activationEmailTemplateService: ActivationEmailTemplatesOverviewService,
    private activationEmailService: ActivationEmailService,
    private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.loadActivationEmailTemplates();
    this._subscription.add(this.configurationFormService.transferTypeChanged.subscribe(() => {
      if (this.configurationFormService.transferType != null && this.configurationFormService.transferType != ExportInterfaceConfigurationTransferType.FTPClient) {
        this.showActivationEmailOption = true;
        this.refreshActivationEmailTemplates();
      }
      else {
        this.showActivationEmailOption = false;
      }
    }));

    this._subscription.add(this.configurationFormService.configurationFormGeneral.controls.mode.valueChanges.subscribe(value => {
      this.setExcludeMembersForType();
      this.setExcludeMebmbersForTransferType();
    }));
    this.setExcludeMembersForType();
    this.setExcludeMebmbersForTransferType();

    this._subscription.add(this.configurationFormService.configurationFormGeneral.controls.type.valueChanges.subscribe(value => {
      // if (value is customer connection, then disable transfer type selection)
      if (value === ExportInterfaceConfigurationType.CustomerConnection) {
        // set transfer type to Email and disable
        this.configurationFormService.configurationFormGeneral.controls.transferType.setValue(ExportInterfaceConfigurationTransferType.Email);
        this.configurationFormService.configurationFormGeneral.controls.transferType.disable();
      } else if (value === ExportInterfaceConfigurationType.XMLOrder) {
        this.configurationFormService.configurationFormGeneral.controls.transferType.setValue(ExportInterfaceConfigurationTransferType.SFTPServerOnly);
        this.configurationFormService.configurationFormGeneral.controls.transferType.disable();
      } else {
        this.configurationFormService.configurationFormGeneral.controls.transferType.enable();
      }
    }));
  }

  private loadActivationEmailTemplates(): void {
    this.activationEmailTemplates = this.activationEmailTemplateService.getActivationEmailTemplatesForTransferType(this.configurationFormService.transferType);
  }

  private refreshActivationEmailTemplates(): void {
    this.activationEmailTemplates = this.activationEmailTemplateService
      .getActivationEmailTemplatesForTransferType(this.configurationFormService.transferType);
  }

  public loadEmailPreview(): void {
    var generateActivationEmailsDTO: GenerateActivationEmailsDTO = new GenerateActivationEmailsDTO();
    generateActivationEmailsDTO.configurationId = this.configurationFormService.configurationDetailsDTO.id;
    generateActivationEmailsDTO.templateId = this.configurationFormGeneral.get('activationEmailTemplateId').value;
    this._subscription.add(
      this.activationEmailService.generateActivationEmails(generateActivationEmailsDTO)
        .subscribe((activationEmails: ExportInterfaceActivationEmailDTO[]) => {
          this.activationEmails = activationEmails;
          if (activationEmails?.length > 0) {
            this.showPreview = true;
            this.currentActivationEmailIndex = 0;
            this.cdr.detectChanges();
          }
        }));
  }

  public onPreviewClosed(): void {
    this.showPreview = false;
    this.cdr.detectChanges();
  }

  public onSendEmailClickd(activationEmailDTO: ExportInterfaceActivationEmailDTO): void {
    this.activationEmailService.sendActivationEmail(activationEmailDTO).subscribe(() => {
      this.configurationFormGeneral.get("activationEmailLastSentOn").setValue(DateTime.now().toFormat("dd.MM.yyyy HH:mm:ss"));
      this.configurationFormService.configurationDetailsDTO.activationEmailLastSentOn = DateTime.now();
      this.showPreview = false;
      this.cdr.detectChanges();
    });
  }

  public onsendAllEmailClicked(activationEmailsDTO: ExportInterfaceActivationEmailDTO[]): void {
    this.activationEmailService.sendActivationEmails(activationEmailsDTO).subscribe(() => {
      this.configurationFormGeneral.get("activationEmailLastSentOn").setValue(DateTime.now().toFormat("dd.MM.yyyy HH:mm:ss"));
      this.showPreview = false;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  private setExcludeMembersForType() {
    if (this.isResponseMode()) {
      this.excludeTypeEnumMembers = Object.keys(ExportInterfaceConfigurationType)
      .filter(key => !isNaN(Number(key)))
      .map(key => Number(key))
      .filter(type => type !== ExportInterfaceConfigurationType.ImportSalesOrderResponse);

    } else {
      this.excludeTypeEnumMembers = [ExportInterfaceConfigurationType.ImportSalesOrderResponse]
    }
  }

  private setExcludeMebmbersForTransferType() {
    if (this.isResponseMode()) {
      this.excludeTransferTypeEnumMembers = [ExportInterfaceConfigurationTransferType.AS2, ExportInterfaceConfigurationTransferType.REST, ExportInterfaceConfigurationTransferType.SFTPServerOnly]
    } else {
      this.excludeTransferTypeEnumMembers = []
    }
  }
}
